<template>
  <div>
    <BaseListPage
        :name="name"
        :no-condition="false"
        :base-query="baseQuery"
        :store-path="storePath"
        :response-path="responsePath"
        :save-key="saveKey"
        :is-parent-loaded="isParentLoaded"
        :table-set-list="tableSetList"
        :table-data-list="sortedList"
        title="メールテンプレート一覧"
        title-class="mailtemplate-header"
        icon="mailtemplate"
        table-class="row-clickable-table"
        @row-click="onRowClick">
      <span slot="headerAction"
          class="action_button">
        <el-dropdown
            v-show="$isFunctionShown(define.PermissionKeys.MailTemplate.List.NEW)"
            @command="actionCommand">
          <el-button size="small">
            アクション
            <i class="el-icon-arrow-down el-icon--right" />
          </el-button>
          <el-dropdown-menu slot="dropdown"
              class="header-action-menu">
            <el-dropdown-item
                v-if="$isFunctionShown(define.PermissionKeys.MailTemplate.List.NEW)"
                :disabled="$isFunctionDisabled(define.PermissionKeys.MailTemplate.List.NEW)"
                :command="define.MAIL_TEMPLATE_MODE.NEW">
              新規登録
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </span>
    </BaseListPage>
    <MailTemplateBaseDialog
        width="70vw"
        class="mailTemplateDialog"
        :title="mailTmeplateTitle"
        :mail-mode="mode"
        :disable-save-button="!isChanged"
        :show-only-close-button="false"
        :show-close="false"
        :before-close="beforeClose"
        :visible.sync="mailTemplateDialogVisible"
        @ok="ok"
        @cancel="cancel"
        @close="close"
        @closed="closed"
        @regist="registTemplate"
        @edit="editTemplate"
        @delete="deleteTemplate">
      <template slot="contents">
        <!-- メールテンプレート情報 -->
        <el-row>
          <el-col :span="24">
            <BaseTableDetail
                class="mail-template-item"
                :table-set-list="mailTemplateInfo"
                :display-item="mailTemplateData"
                :editable="editable" />
          </el-col>
        </el-row>
      </template>
    </MailTemplateBaseDialog>
  </div>
</template>
<style scoped>

.mail-template-item ::v-deep table {
  width: 100%;
}
.mail-template-item ::v-deep th,
.mail-template-item ::v-deep td {
    padding: 5px 15px;
    font-size: 14px;
    text-align: left;
    vertical-align: top;
}
.mail-template-item ::v-deep th {
    width: 230px;
    padding-right: 0;
    height: 28px;
}
</style>

<script>
import _ from 'lodash'
import { mapState, mapMutations, mapGetters } from 'vuex'
import * as define from '@/define'
import * as query from '@/queries/mailTemplate'
import common from '@/utils/common'
import message from '@/utils/message'
import compare from '@/utils/compareUtils'
import * as config from '@/config'
import x from '@/components/mixins/mixin'
import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import BaseListPage from '@/components/organisms/BaseListPage'
import BaseTableDetail from '@/components/molecules/BaseTableDetail'
import MailTemplateBaseDialog from '@/components/pages/mail/MailTemplateBaseDialog'
import confirmDiscardOnLeave from '@/components/mixins/confirmDiscardOnLeave'

const EDITABLE = Object.freeze({
  [define.MAIL_TEMPLATE_MODE.NORMAL]: Object.freeze({}),
  [define.MAIL_TEMPLATE_MODE.DELETE]: Object.freeze({}),
  [define.MAIL_TEMPLATE_MODE.CONFIRM]: Object.freeze({}),
  [define.MAIL_TEMPLATE_MODE.EDIT]: Object.freeze({ site: true, type: true, templateName: true, from: true, fromMine: true, to: true, cc: true, bcc: true, replyTo: true, subject: true, templateText: true, templateHtml: true }),
  [define.MAIL_TEMPLATE_MODE.NEW]: Object.freeze({ site: true, type: true, templateName: true, from: true, fromMine: true, to: true, cc: true, bcc: true, replyTo: true, subject: true, templateText: true, templateHtml: true })
})

export default {
  name: 'MailTemplateList',
  components: {
    BaseListPage,
    BaseTableDetail,
    MailTemplateBaseDialog
  },
  mixins: [x, confirmDiscardOnLeave],
  provide () {
    return {
      parentValidator: this.$validator
    }
  },
  data () {
    return {
      mode: define.MAIL_TEMPLATE_MODE.NORMAL,
      mailTitle: '',
      formatMailTemplate: {
        node: {
          id: '',
          uuid: undefined,
          site: {
            code: '',
            name: ''
          },
          type: '',
          templateName: '',
          from: '',
          fromMine: '',
          to: '',
          cc: '',
          bcc: '',
          replyTo: '',
          subject: '',
          templateText: '',
          templateHtml: '',
          updatedAt: ''
        }
      },
      originalData: {},
      localIsParentLoaded: false,
      mailTemplateData: {},
      mailTemplateDialogVisible: false,
      isChanged: false,
      beforeClose: null
    }
  },
  computed: {
    define: () => define,
    ...mapState({
      listIndex: (state) => state.MailTemplate.listIndex,
      tableSetList: (state) => state.MailTemplate.tableSetList
    }),
    ...mapGetters({
      getSortedList: 'sortedList',
      getServiceSiteConfig: 'getServiceSiteConfig',
      searchConditions: 'searchConditions'
    }),
    ...mapGetters('LocalStorage', {
      sortCondition: 'sortCondition'
    }),
    name () {
      return 'mailTemplateList'
    },
    storePath () {
      return 'MailTemplate'
    },
    saveKey () {
      return 'mailTemplateCondition'
    },
    responsePath () {
      return 'data.mailTemplates.edges'
    },
    returnPath () {
      return '/settings/mailTemplate'
    },
    baseQuery () {
      return query.GRAPH_QL_QUERY_MAIL_TEMPLATE_LIST
    },
    sortedList () {
      return this.getSortedList(this.storePath, this.sortCondition(this.name))
    },
    _tableSetList () {
      return [
        {
          type: 'String',
          label: 'メールテンプレート名称',
          path: 'node.templateName',
          sortable: 'custom',
          funcId: define.PermissionKeys.MailTemplate.LIST && define.PermissionKeys.MailTemplate.List.NAME
        },
        {
          type: 'String',
          label: '差出人（from）',
          path: 'node.from',
          sortable: 'custom',
          funcId: define.PermissionKeys.MailTemplate.LIST && define.PermissionKeys.MailTemplate.List.FROM
        },
        {
          type: 'String',
          label: 'TO',
          path: 'node.to',
          sortable: 'custom',
          funcId: define.PermissionKeys.MailTemplate.LIST && define.PermissionKeys.MailTemplate.List.TO
        },
        {
          type: 'String',
          label: '件名',
          path: 'node.subject',
          sortable: 'custom',
          funcId: define.PermissionKeys.MailTemplate.LIST && define.PermissionKeys.MailTemplate.List.SUBJECT
        }
      ]
    },
    mailTemplateInfo () {
      return [
        {
          label: 'サイト名',
          properties: [
            { path: 'node.site.code', elementType: ELEMENT_TYPE.SELECT_BOX, type: 'String', codeName: 'sites', validateRule: 'required',
              selectCode: 'sites', validateAs: 'サイト名', editableId: 'site', funcId: define.PermissionKeys.MailTemplate.List.SITE }
          ]
        },
        {
          label: '種別',
          properties: [
            { path: 'node.type', type: 'String', editableId: 'type', validateAs: '種別',
              selectCode: 'm_mail_template.type', codeName: 'm_mail_template.type',
              elementType: ELEMENT_TYPE.SELECT_BOX,
              funcId: define.PermissionKeys.MailTemplate.List.TYPE
            }
          ]
        },
        {
          label: 'メールテンプレート名称',
          properties: [
            { path: 'node.templateName', type: 'String', editableId: 'templateName', elementType: ELEMENT_TYPE.INPUT,
              validateRule: 'required|max:256', validateAs: 'メールテンプレート名称', funcId: define.PermissionKeys.MailTemplate.List.NAME }
          ]
        },
        {
          label: '差出人（from）',
          properties: [
            { path: 'node.from', type: 'String', editableId: 'from', elementType: ELEMENT_TYPE.INPUT,
              validateRule: 'max:256', validateAs: '差出人（from）', funcId: define.PermissionKeys.MailTemplate.List.FROM }
          ]
        },
        {
          label: '差出人名',
          properties: [
            { path: 'node.fromMine', type: 'String', editableId: 'fromMine', elementType: ELEMENT_TYPE.INPUT,
              validateRule: 'max:256', validateAs: '差出人名', funcId: define.PermissionKeys.MailTemplate.List.FROM_MINE }
          ]
        },
        {
          id: 'to',
          label: 'TO',
          properties: [
            {
              path: 'node.to', type: 'String', elementType: ELEMENT_TYPE.CELL, validateAs: 'TO',
              properties: this.validToAddrObjData, class: 'to',
              funcId: define.PermissionKeys.MailTemplate.List.TO
            }
          ]
        },
        {
          id: 'cc',
          label: 'CC',
          properties: [
            {
              path: 'node.cc', type: 'String', elementType: ELEMENT_TYPE.CELL, validateAs: 'CC',
              properties: this.validCcAddrObjData, class: 'cc',
              funcId: define.PermissionKeys.MailTemplate.List.CC
            }
          ]
        },
        {
          id: 'bcc',
          label: 'BCC',
          properties: [
            {
              path: 'node.bcc', ttype: 'String', elementType: ELEMENT_TYPE.CELL, validateAs: 'BCC',
              properties: this.validBccAddrObjData, class: 'bcc',
              funcId: define.PermissionKeys.MailTemplate.List.BCC
            }
          ]
        },
        {
          id: 'replyTo',
          label: '返信先',
          properties: [
            {
              path: 'node.replyTo', type: 'String', elementType: ELEMENT_TYPE.CELL, validateAs: '返信先',
              properties: this.validReplyToAddrObjData, class: 'replyTo',
              funcId: define.PermissionKeys.MailTemplate.List.REPLY_TO
            }
          ]
        },
        {
          label: '件名',
          properties: [
            { path: 'node.subject', type: 'String', editableId: 'subject', elementType: ELEMENT_TYPE.INPUT,
              validateRule: 'required|max:256', validateAs: '件名', funcId: define.PermissionKeys.MailTemplate.List.SUBJECT }
          ]
        },
        {
          label: 'テキストテンプレート',
          properties: [
            { path: 'node.templateText', type: 'String', editableId: 'templateText', elementType: ELEMENT_TYPE.INPUT_TEXT_AREA,
              validateAs: 'テキストテンプレート', validateRule: 'required|max:8000', lineBreak: true, funcId: define.PermissionKeys.MailTemplate.List.TEXT, rows: 20 }
          ]
        },
        {
          label: 'HTMLテンプレート',
          properties: [
            { path: 'node.templateHtml', type: 'String', editableId: 'templateHtml', elementType: ELEMENT_TYPE.INPUT_TEXT_AREA,
              validateAs: 'HTMLテンプレート', validateRule: 'max:8000', lineBreak: true, funcId: define.PermissionKeys.MailTemplate.List.HTML, rows: 20 }
          ]
        }
      ]
    },
    mailTmeplateTitle () {
      return this.mailTitle
    },
    editable () {
      return EDITABLE[this.mode]
    },
    editableAddrs () {
      return this.mode === define.MAIL_TEMPLATE_MODE.NEW || this.mode === define.MAIL_TEMPLATE_MODE.EDIT
    },
    isParentLoaded () {
      return this.localIsParentLoaded
    },
    validToAddrObjData () {
      return this.getAddrObjData('to')
    },
    validCcAddrObjData () {
      return this.getAddrObjData('cc')
    },
    validBccAddrObjData () {
      return this.getAddrObjData('bcc')
    },
    validReplyToAddrObjData () {
      return this.getAddrObjData('replyTo')
    }
  },
  watch: {
    mailTemplateData: {
      handler: function (val, oldVal) {
        let vm = this
        vm.isChanged = true
      },
      deep: true
    }
  },
  async created () {
    this.$loadingService.show()
    try {
      this.$store.dispatch({
        type: 'set',
        states: {
          'MailTemplate/tableSetList': this._tableSetList
        }
      })
    } catch (error) {
      this.$notify.error({ error: error })
      throw error
    } finally {
      this.$loadingService.close()
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.beforeClose = () => {
        switch (this.mode) {
          case define.MAIL_TEMPLATE_MODE.NORMAL:
            this.ok()
            break
          case define.MAIL_TEMPLATE_MODE.NEW:
          case define.MAIL_TEMPLATE_MODE.EDIT:
            this.cancel()
            break
          case define.MAIL_TEMPLATE_MODE.CONFIRM:
          case define.MAIL_TEMPLATE_MODE.DELETE:
          default:
            break
        }
      }
    })
  },
  methods: {
    ...mapMutations('MailTemplate', {
      updateListItem: 'update',
      deleteListItem: 'delete'
    }),
    onRowClick (row, column, event) {
      // 詳細表示
      this.mailTitle = 'メールテンプレート詳細'
      this.mailTemplateDialogVisible = true
      this.originalData = row
      this.mailTemplateData = _.cloneDeep(this.originalData)
    },
    /**
     * アクションボタン
     * @param {Number} mode モード
     */
    actionCommand: async function (mode) {
      switch (mode) {
        case define.MAIL_TEMPLATE_MODE.NEW:
          this.mailTitle = 'メールテンプレート追加'
          this.mailTemplateData = _.cloneDeep(this.formatMailTemplate)
          this.mailTemplateDialogVisible = true
          this.mode = define.MAIL_TEMPLATE_MODE.NEW
          break
        default:
          break
      }
    },
    ok () {
      if (this.mode !== define.MAIL_TEMPLATE_MODE.CONFIRM) {
        this.mailTemplateDialogVisible = false
      }
    },
    close () {
      this.beforeClose()
    },
    closed () {
      this.mode = define.MAIL_TEMPLATE_MODE.NORMAL
    },
    cancel () {
      const cancelEdit = () => {
        if (this.mode === define.MAIL_TEMPLATE_MODE.NEW) {
          this.mailTemplateDialogVisible = false
        } else {
          this.mailTitle = 'メールテンプレート詳細'
        }
        this.mode = define.MAIL_TEMPLATE_MODE.NORMAL
      }
      // 変更があった場合は確認ダイアログを表示
      if (this.isChanged) {
        this.$confirm(
          this.mode === define.MAIL_TEMPLATE_MODE.NEW
            ? 'メールテンプレート登録を中止しますか？'
            : '変更を破棄しますか？',
          define.MESSAGE_TITLE,
          {
            confirmButtonText: 'はい',
            cancelButtonText: 'いいえ',
            type: 'warning'
          }
        ).then(() => {
          cancelEdit()
          this.mailTemplateData = _.cloneDeep(this.originalData)
        }).catch(() => {})
      } else {
        cancelEdit()
      }
    },
    registTemplate () {
      if (this.mode === define.MAIL_TEMPLATE_MODE.NEW) {
        this.$validator.validate().then(result => {
          if (result) {
            this.$confirm(
              '保存しますか？',
              define.MESSAGE_TITLE,
              {
                confirmButtonText: 'OK',
                cancelButtonText: 'Cancel',
                type: 'warning'
              }
            ).then(() => {
              // 登録・更新API呼び出し
              this.updateOrInsertMailTemplate()
            }).catch(() => {})
          }
        })
      } else if (this.mode === define.MAIL_TEMPLATE_MODE.EDIT) {
        this.$validator.validate().then(result => {
          if (result) {
            this.$confirm(
              '保存しますか？',
              define.MESSAGE_TITLE,
              {
                confirmButtonText: 'OK',
                cancelButtonText: 'Cancel',
                type: 'warning'
              }
            ).then(() => {
              // 登録・更新API呼び出し
              this.updateOrInsertMailTemplate()
              this.ok()
            }).catch(() => {})
          }
        })
      }
    },
    editTemplate () {
      this.mailTitle = 'メールテンプレート編集'
      this.mode = define.MAIL_TEMPLATE_MODE.EDIT
      this.isChanged = false
    },
    deleteTemplate () {
      this.$confirm(
        'メールテンプレートを削除します。\r\nよろしいですか？',
        define.MESSAGE_TITLE,
        {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      ).then(() => {
        // メールテンプレート削除処理（APIコール）
        this.deleteMailTemplate()
      }).catch(() => {})
    },
    getAddrObjData (kind) {
      let objData = []
      let data = this.mailTemplateData.node
      if (this.mailTemplateData.node != null) {
        let addrs
        let tmpAddrs
        let tempId
        let validateAs = ''

        switch (kind) {
          case 'to':
            tmpAddrs = data.to
            tempId = 'validToAddrTxt'
            validateAs = 'TO'
            break
          case 'cc':
            tmpAddrs = data.cc
            tempId = 'validCcAddrTxt'
            validateAs = 'CC'
            break
          case 'bcc':
            tmpAddrs = data.bcc
            tempId = 'validBccAddrTxt'
            validateAs = 'BCC'
            break
          case 'replyTo':
            tmpAddrs = data.replyTo
            tempId = 'validReplyToAddrTxt'
            validateAs = '返信先'
            break
          default:
            break
        }

        if (tmpAddrs != null) {
          addrs = tmpAddrs.split(',')
          addrs.forEach((addr) => {
            const index = objData.length
            let propertiesData = [{
              id: tempId,
              type: 'String',
              elementType: ELEMENT_TYPE.INPUT,
              path: 'addr',
              validateAs: validateAs,
              validateRule: 'max:1000',
              validateScope: kind,
              displayItem: { addr },
              change: (event) => { this.onChangeAddrs(event, index, data, kind, tempId) },
              editable: true,
              editableCondition: () => this.editableAddrs
            }]

            if (this.editableAddrs) {
              // 行削除用ボタン
              propertiesData.push({
                type: 'String', elementType: ELEMENT_TYPE.BUTTON, class: 'delBtn',
                icon: 'fas fa-times-circle', click: (event) => { this.onClickAddrDelBtn(data, tmpAddrs, kind, index) }
              })
              // 追加ボタン（新規登録・編集時に使用可能。アドレスの最終行に表示）
              if (addrs.length === index + 1) {
                propertiesData.push({
                  id: 'addBtn',
                  type: 'String', elementType: ELEMENT_TYPE.BUTTON, class: 'addBtn',
                  icon: 'fas fa-plus-circle', size: 'small', click: (event) => { this.onClickAddrAddBtn(data, kind) }
                })
              }
            }
            objData.push({
            // カスタムタイプのセルを指定
              type: 'String', elementType: ELEMENT_TYPE.CELL,
              properties: propertiesData,
              index: index,
              horizontal: true
            })
          })
        } else {
        // 追加ボタン（新規登録・編集時に使用可能。アドレスが1つも存在しないとき下部に表示）
          if (this.editableAddrs) {
            objData.push({
              id: 'addBtn',
              type: 'String', elementType: ELEMENT_TYPE.BUTTON, class: 'addBtn',
              icon: 'fas fa-plus-circle', size: 'small', click: (event) => { this.onClickAddrAddBtn(data, kind) }
            })
          }
        }
      } else {
        // 追加ボタン（新規登録・編集時に使用可能。アドレスが1つも存在しないとき下部に表示）
        if (this.editableAddrs) {
          objData.push({
            id: 'addBtn',
            type: 'String', elementType: ELEMENT_TYPE.BUTTON, class: 'addBtn',
            icon: 'fas fa-plus-circle', size: 'small', click: (event) => { this.onClickAddrAddBtn(data, kind) }
          })
        }
      }

      return objData

    },
    onClickAddrDelBtn (data, addrs, kind, index) {
      let addr = addrs.split(',')
      addr = addr.filter((val, key) => {
        return key !== index
      })
      switch (kind) {
        case 'to':
          data.to = addr.length === 0 ? null : addr.join(',')
          break
        case 'cc':
          data.cc = addr.length === 0 ? null : addr.join(',')
          break
        case 'bcc':
          data.bcc = addr.length === 0 ? null : addr.join(',')
          break
        case 'replyTo':
          data.replyTo = addr.length === 0 ? null : addr.join(',')
          break
        default:
          break
      }
      // 行番号が変わり、メッセージの出力箇所が不正となるため、再度全行バリデーションチェックを行う。
      this.addrValidationCheck()
    },
    onClickAddrAddBtn (data, kind) {

      if (data != null) {
        switch (kind) {
          case 'to':
            if (data.to != null) {
              // すでに行があるとき
              data.to += ','
            } else {
              // 1行もないとき
              data.to = ''
            }
            break
          case 'cc':
            if (data.cc != null) {
              data.cc += ','
            } else {
              data.cc = ''
            }
            break
          case 'bcc':
            if (data.bcc != null) {
              data.bcc += ','
            } else {
              data.bcc = ''
            }
            break
          case 'replyTo':
            if (data.replyTo != null) {
              data.replyTo += ','
            } else {
              data.replyTo = ''
            }
            break
          default:
            break
        }
      } else {
        // 新規
        switch (kind) {
          case 'to':
            data.to = ''
            break
          case 'cc':
            data.cc = ''
            break
          case 'bcc':
            data.bcc = ''
            break
          case 'replyTo':
            data.replyTo = ''
            break
          default:
            break
        }
      }
    },
    onChangeAddrs (event, index, data, kind, id) {
      // 現在の値更新
      let newAddrs = []
      const addrRows = this.mailTemplateInfo.find((obj) => { return obj.id === kind }).properties[0].properties
      Object.keys(addrRows).forEach((key) => {
        if (key === String(index)) {
          newAddrs.push(event.replace(/,/g, ''))
        } else {
          if (addrRows[key].id !== 'addBtn') {
            newAddrs.push(addrRows[key].properties.find((obj) => { return obj.id === id }).displayItem.addr.replace(/,/g, ''))
          }
        }
      })
      switch (kind) {
        case 'to':
          data.to = newAddrs.join(',')
          break
        case 'cc':
          data.cc = newAddrs.join(',')
          break
        case 'bcc':
          data.bcc = newAddrs.join(',')
          break
        case 'replyTo':
          data.replyTo = newAddrs.join(',')
          break
        default:
          break
      }
      this.addrValidationCheck()
    },
    // 値変更により重複チェックが解消されたとき、もう一方のエラーを解除する必要があるため、全行バリデーションチェックを行う。
    addrValidationCheck () {
      setTimeout(() => {
        this.$validator.validate('to.*')
        this.$validator.validate('cc.*')
        this.$validator.validate('bcc.*')
        this.$validator.validate('replyTo.*')
      })
    },
    /**
     * mailTemplateData からメールテンプレート登録APIのリクエストパラメータを作成する.
     */
    buildRequestForInsert () {
      let requestBody = {
        siteCode: this.mailTemplateData.node.site.code,
        type: this.mailTemplateData.node.type,
        templateName: this.mailTemplateData.node.templateName,
        from: this.mailTemplateData.node.from,
        fromMine: this.mailTemplateData.node.fromMine,
        to: this.mailTemplateData.node.to,
        cc: this.mailTemplateData.node.cc,
        bcc: this.mailTemplateData.node.bcc,
        replyTo: this.mailTemplateData.node.replyTo,
        subject: this.mailTemplateData.node.subject,
        templateText: this.mailTemplateData.node.templateText,
        templateHtml: this.mailTemplateData.node.templateHtml
      }
      return requestBody
    },
    /**
     * mailTemplateData からメールテンプレート更新APIのリクエストパラメータを作成する.
     * originalDataとの差分のみ戻り値とする.
     */
    buildRequestForUpdate () {
      const diff = compare.diff(this.originalData.node, this.mailTemplateData.node)
      let requestBody = _.pick(diff, ['siteCode', 'type', 'templateName', 'from', 'fromMine', 'to', 'cc', 'bcc', 'replyTo', 'subject', 'templateText', 'templateHtml'])
      return requestBody
    },
    async updateOrInsertMailTemplate () {
      try {
        // メールアドレスの不要な空行の削除
        this.mailTemplateData.node.to = this.mailTemplateData.node.to ? this.mailTemplateData.node.to.split(/[\s,]+/).filter((val) => val).join(',') : this.mailTemplateData.node.to
        this.mailTemplateData.node.cc = this.mailTemplateData.node.cc ? this.mailTemplateData.node.cc.split(/[\s,]+/).filter((val) => val).join(',') : this.mailTemplateData.node.cc
        this.mailTemplateData.node.bcc = this.mailTemplateData.node.bcc ? this.mailTemplateData.node.bcc.split(/[\s,]+/).filter((val) => val).join(',') : this.mailTemplateData.node.bcc
        this.mailTemplateData.node.replyTo = this.mailTemplateData.node.replyTo ? this.mailTemplateData.node.replyTo.split(/[\s,]+/).filter((val) => val).join(',') : this.mailTemplateData.node.replyTo
        this.$loadingService.show()
        await this.updateOrInsertMailTemplateApi()
        this.isChanged = false
        if (this.mode === define.MAIL_TEMPLATE_MODE.CONFIRM) {
          this.$alert(
            'メールテンプレートを更新しました。',
            define.MESSAGE_TITLE,
            { type: 'warning', showClose: false }
          )
        }
        this.$notify.success('保存しました。')
        this.mode = define.MAIL_TEMPLATE_MODE.NORMAL
        this.mailTemplateDialogVisible = false
        this.refreshList()
      } catch (error) {
        const data = _.get(error, 'response.data')
        if (data == null) {
          common.addError(error, { msg: message.MSG_502() })
        } else {
          if (this.mode === define.MAIL_TEMPLATE_MODE.NEW) {
            common.addError(error, { msg: message.MSG_550() })
          } else {
            common.addError(error, { msg: message.MSG_551() })
          }
        }
        this.$notify.error({ error: error })
        throw error
      } finally {
        this.$loadingService.close()
      }
    },
    async deleteMailTemplate () {
      try {
        this.$loadingService.show()
        await this.deleteMailTemplateApi()
        this.deleteListItem(this.mailTemplateData.node.uuid)
        this.$notify.success('メールテンプレートを削除しました。')
        this.mailTemplateDialogVisible = false
        this.mode = define.MAIL_TEMPLATE_MODE.NORMAL
        this.isChanged = false
        this.refreshList()
      } catch (error) {
        const data = _.get(error, 'response.data')
        if (data == null) {
          common.addError(error, { msg: message.MSG_502() })
        } else {
          common.addError(error, { msg: message.MSG_552() })
        }
        this.$notify.error({ error: error })
        throw error
      } finally {
        this.$loadingService.close()
      }
    },
    /**
     * メールテンプレート登録 API またはメールテンプレート更新 APIを呼び出す
     */
    async updateOrInsertMailTemplateApi () {
      let url
      let method
      let data
      let heads = {}
      if (this.mode === define.MAIL_TEMPLATE_MODE.NEW) {
        url = config.MAIL_TEMPLATE_URL
        method = 'post'
        data = this.buildRequestForInsert()
      } else {
        heads = { 'X-CCS-Last-Updated-At': this.mailTemplateData.node.updatedAt }
        url = config.MAIL_TEMPLATE_URL + '/' + this.mailTemplateData.node.uuid
        method = 'put'
        data = this.buildRequestForUpdate()
      }
      const response = await common.apiAxios(url, { method, data, heads })
      return response
    },
    async deleteMailTemplateApi () {
      const heads = { 'X-CCS-Last-Updated-At': this.mailTemplateData.node.updatedAt }
      const url = config.MAIL_TEMPLATE_URL + '/' + this.mailTemplateData.node.uuid
      const method = 'delete'
      const response = await common.apiAxios(url, { method, heads })
      return response
    },
    async refreshList () {
      try {
        const tdLists = this.storePath + '/tdLists'
        let queryCondition = this.searchConditions(this.storePath)
        const aquery = common.addGraphQlCondition(query.GRAPH_QL_QUERY_MAIL_TEMPLATE_LIST, queryCondition)
        const res = await common.postGraphQl(aquery)
        this.$store.dispatch({
          type: 'set',
          states: {
            [tdLists]: common.getValueByPath(res.data, this.responsePath)
          }
        })
      } catch (error) {
        this.$notify.error({ error: error })
        throw error
      }
    }
  }
}
</script>
